import { useState } from "react";
import { Link } from "react-router-dom";
import { batchDeleteInstances, listInstances, updateExpiresAt } from "../../api/fleet";
import type { FleetInstanceStatus, FleetRegion, ListFleetInstancesFilters } from "../../api/types";
import { useAsync } from "../../hooks/useAsync";
import { Pagination } from "../../components/Pagination";
import { useAuth } from "../../auth/AuthContext";
import { formatBeijingDate } from "../../utils/datetime";

const STATUSES: FleetInstanceStatus[] = ["PROVISIONING", "READY", "PROVISION_FAILED", "DELETED"];
const REGIONS: { value: FleetRegion; label: string }[] = [
  { value: "ap-northeast-1", label: "东京(ap-northeast-1)" },
  { value: "ap-southeast-1", label: "新加坡(ap-southeast-1)" },
];
const PAGE_SIZE = 20;
const MAX_BATCH_DELETE = 500;

const EMPTY_FILTERS: ListFleetInstancesFilters = {};

/** 日期字符串(YYYY-MM-DD 或含时间的 ISO)转 <input type="date"> 所需的 YYYY-MM-DD。 */
function toDateInput(dateStr?: string): string {
  if (!dateStr) return "";
  return dateStr.slice(0, 10);
}

/**
 * 到期时间列的行内编辑器,交互模式与 `UsersPage.tsx` 的 `ExpiryEditor` 一致
 * (点"修改" -> 出现日期输入框 + 保存/取消),用于满足"到期时间增加手动
 * 修改的按钮"这一需求(§7.3.1:修改到期日不影响新语义下"北京时间 23:50"
 * 的回收判定逻辑,只是换了个 `expires_at` 日期)。
 */
function ExpiryCell({
  value,
  onSave,
  disabled,
}: {
  value?: string;
  onSave: (dateStr: string) => Promise<void>;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [dateStr, setDateStr] = useState(toDateInput(value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editing) {
    return (
      <span>
        {formatBeijingDate(value)}{" "}
        {!disabled && (
          <button
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={() => {
              setDateStr(toDateInput(value));
              setEditing(true);
              setError(null);
            }}
          >
            修改
          </button>
        )}
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input
        type="date"
        value={dateStr}
        onChange={(e) => setDateStr(e.target.value)}
        style={{ fontSize: 12, padding: "2px 4px" }}
      />
      <button
        style={{ fontSize: 11, padding: "2px 6px" }}
        disabled={saving || !dateStr}
        onClick={async () => {
          setSaving(true);
          setError(null);
          try {
            await onSave(dateStr);
            setEditing(false);
          } catch (err) {
            setError(err instanceof Error ? err.message : "保存失败");
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving ? "保存中..." : "保存"}
      </button>
      <button style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => setEditing(false)} disabled={saving}>
        取消
      </button>
      {error && <span style={{ color: "#dc2626", fontSize: 11 }}>{error}</span>}
    </span>
  );
}

/**
 * Kiro-Go 舰队列表页,对应 design.md > 前端页面设计 `FleetListPage.tsx`
 * (Requirements 6.1-6.8, 10.2)。检索/筛选采用草稿态设计,与 `UsersPage.tsx`
 * v2.15 的"点查询才生效"模式一致;多选 + 批量删除需二次确认;凭证漂移标识
 * 无需额外交互即可见。
 */
export function FleetListPage() {
  const { canWrite } = useAuth();

  const [filterDraft, setFilterDraft] = useState<ListFleetInstancesFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ListFleetInstancesFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [rangeError, setRangeError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [busyInstanceId, setBusyInstanceId] = useState<string | null>(null);

  const instances = useAsync(
    () => listInstances({ ...appliedFilters, page, pageSize: PAGE_SIZE }),
    [appliedFilters, page]
  );

  const handleApplyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      filterDraft.expiryFrom &&
      filterDraft.expiryTo &&
      filterDraft.expiryFrom > filterDraft.expiryTo
    ) {
      setRangeError("到期时间区间起始值不能晚于结束值,筛选未应用");
      return;
    }
    setRangeError(null);
    setAppliedFilters(filterDraft);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setRangeError(null);
    setPage(1);
  };

  const toggleSelect = (instanceId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(instanceId)) next.delete(instanceId);
      else next.add(instanceId);
      return next;
    });
    setSelectionError(null);
  };

  const handleBatchDeleteClick = () => {
    if (selected.size === 0) {
      setSelectionError("请先勾选至少一条记录");
      return;
    }
    if (selected.size > MAX_BATCH_DELETE) {
      setSelectionError(`单次勾选并提交的记录数量上限为 ${MAX_BATCH_DELETE} 条`);
      return;
    }
    setSelectionError(null);
    setConfirmingDelete(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await batchDeleteInstances(Array.from(selected));
      setSelected(new Set());
      setConfirmingDelete(false);
      instances.reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "批量删除失败");
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateExpiresAt = async (instanceId: string, dateStr: string) => {
    setBusyInstanceId(instanceId);
    try {
      await updateExpiresAt(instanceId, dateStr);
      instances.reload();
    } finally {
      setBusyInstanceId(null);
    }
  };

  return (
    <div>
      <h2 className="section-title">Kiro-Go 实例列表</h2>

      <form onSubmit={handleApplyFilters} className="card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div className="form-row" style={{ margin: 0 }}>
            <label>检索</label>
            <input
              value={filterDraft.search ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, search: e.target.value || undefined })}
              placeholder="邮箱/名称备注子串匹配"
              style={{ width: 200 }}
            />
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>状态</label>
            <select
              value={filterDraft.status ?? ""}
              onChange={(e) =>
                setFilterDraft({ ...filterDraft, status: (e.target.value || undefined) as FleetInstanceStatus | undefined })
              }
            >
              <option value="">全部</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>区域</label>
            <select
              value={filterDraft.region ?? ""}
              onChange={(e) =>
                setFilterDraft({ ...filterDraft, region: (e.target.value || undefined) as FleetRegion | undefined })
              }
            >
              <option value="">全部</option>
              {REGIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ margin: 0 }}>
            <label>到期时间</label>
            <input
              type="date"
              value={filterDraft.expiryFrom ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, expiryFrom: e.target.value || undefined })}
            />
            <span>至</span>
            <input
              type="date"
              value={filterDraft.expiryTo ?? ""}
              onChange={(e) => setFilterDraft({ ...filterDraft, expiryTo: e.target.value || undefined })}
            />
          </div>
          <button type="submit">查询</button>
          <button type="button" onClick={handleResetFilters}>
            重置
          </button>
        </div>
        {rangeError && <div className="error-banner">{rangeError}</div>}
      </form>

      {canWrite && (
        <div className="card" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>已选中 {selected.size} 条</span>
          <button className="danger" onClick={handleBatchDeleteClick} disabled={deleting}>
            批量删除
          </button>
          {selectionError && <span style={{ color: "#dc2626", fontSize: 12 }}>{selectionError}</span>}
        </div>
      )}

      {confirmingDelete && (
        <div className="card" style={{ background: "#fef3c7", border: "1px solid #f59e0b" }} data-testid="batch-delete-confirm">
          <p>
            确认删除已选中的 {selected.size} 条 Kiro-Go 实例记录?此操作会删除对应的 Lightsail
            实例,不可撤销。
          </p>
          {deleteError && <div className="error-banner">{deleteError}</div>}
          <button className="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? "删除中..." : "确认删除"}
          </button>
          <button style={{ marginLeft: 8 }} onClick={() => setConfirmingDelete(false)} disabled={deleting}>
            取消
          </button>
        </div>
      )}

      {instances.loading && <p>加载中...</p>}
      {instances.error && <div className="error-banner">{instances.error}</div>}

      {instances.data && (
        <div className="card">
          {instances.data.invalid_expiry_range && (
            <div className="error-banner">到期时间区间设置无效(起始值晚于结束值),该筛选条件未生效</div>
          )}
          <table>
            <thead>
              <tr>
                {canWrite && <th></th>}
                <th>实例标识</th>
                <th>状态</th>
                <th>凭证状态</th>
                <th>使用人邮箱</th>
                <th>Kiro 账号邮箱</th>
                <th>名称/备注</th>
                <th>区域</th>
                <th>公网 IP</th>
                <th>到期时间</th>
              </tr>
            </thead>
            <tbody>
              {instances.data.instances.map((inst) => (
                <tr key={inst.instance_id}>
                  {canWrite && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(inst.instance_id)}
                        onChange={() => toggleSelect(inst.instance_id)}
                        aria-label={`选择实例 ${inst.instance_id}`}
                      />
                    </td>
                  )}
                  <td>
                    <Link to={`/fleet/instances/${inst.instance_id}`}>{inst.instance_id}</Link>
                  </td>
                  <td>
                    <span className="badge badge-pending">{inst.status}</span>
                  </td>
                  <td>
                    {inst.credential_drift && (
                      <span className="badge badge-warn" data-testid="drift-badge">
                        凭证可能已失效
                      </span>
                    )}
                  </td>
                  <td>{inst.assigned_user_email}</td>
                  <td style={{ fontSize: 12, maxWidth: 200 }}>
                    {inst.kiro_user_emails && inst.kiro_user_emails.length > 0
                      ? inst.kiro_user_emails.join(", ")
                      : <span style={{ color: "#94a3b8" }}>-</span>}
                  </td>
                  <td>{inst.name_note ?? "-"}</td>
                  <td>{inst.region}</td>
                  <td>{inst.public_ip}</td>
                  <td>
                    <ExpiryCell
                      value={inst.expires_at}
                      disabled={!canWrite || busyInstanceId === inst.instance_id}
                      onSave={(dateStr) => handleUpdateExpiresAt(inst.instance_id, dateStr)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {instances.data.instances.length === 0 && <p style={{ color: "#64748b" }}>没有符合条件的实例</p>}
          <Pagination
            page={instances.data.page}
            totalPages={instances.data.total_pages}
            total={instances.data.total}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
